/*
 * (c) Copyright 2024 Calabrio, Inc.
 * All Rights Reserved. www.calabrio.com LICENSED MATERIALS
 * Property of Calabrio, Inc., Minnesota, USA
 *
 * No part of this publication may be reproduced, stored or transmitted,
 * in any form or by any means (electronic, mechanical, photocopying,
 * recording or otherwise) without prior written permission from Calabrio, Inc.
 */

import AnalyticsService from '../../service/analytics';
import WyUtils from '../../library/wy-utils.js';
import WyFilter from '../../library/wy-filter.js';
import WyConstants from '../../library/wy-constants.js';

import moment from 'moment';
import _ from 'lodash';

const state = {
  contactReasonRunStats: null,
  transcriptInfoReference: null,
};

const getters = {
  contactReasonRunStats: (state) => state.contactReasonRunStats,
  transcriptInfoReference: (state) => state.transcriptInfoReference,
};

const mutations = {
  updateTranscriptInfoReference(state, data) {
    if (data) {
      state.transcriptInfoReference = data;
    } else {
      state.transcriptInfoReference = null;
    }
  },
};

const actions = {
  fetchChatTopics({ getters }, options) {
    let payload = Object.assign(WyFilter.filterValues(getters.shareState, getters.dataModel), options.dateRanges);

    if (getters.dataModel.filters.topics && getters.dataModel.filters.topics.value) {
      payload[getters.dataModel.filters.topics.name] = getters.dataModel.filters.topics.value.reduce((list, item) => {
        item.mergedTopics.map((el) => list.push(el));
        return list;
      }, []);
    }
    payload.topics = []; //Always fetch date without topics filter
    if (options && options.maxTopics) payload.maxTopics = options.maxTopics;
    return AnalyticsService.fetchChatTopics(getters.authUser, payload);
  },

  fetchAllChatTopics({ getters }) {
    let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, null);
    if (getters.dataModel.filters.topics && getters.dataModel.filters.topics.value) {
      payload[getters.dataModel.filters.topics.name] = getters.dataModel.filters.topics.value.reduce((list, item) => {
        item.mergedTopics.map((el) => list.push(el));
        return list;
      }, []);
    }
    return AnalyticsService.fetchChatTopics(getters.authUser, payload);
  },

  fetchAllContactReasons({ getters }, payload = {}) {
    return AnalyticsService.fetchAllContactReasons(getters.authUser, payload);
  },

  async fetchChatFilters({ getters, dispatch, rootState }, { pageName }) {
    return new Promise(async (resolve) => {
      const dataModel = rootState.site.pages[pageName].dataModel;

      if (!getters.localStorage.agentData) {
        // Available chatFilters - ['BotToLC', 'LCOnly', 'BOT', 'BotOnlyContained', 'BotOnlyEscalated']
        let defaultCustTypeFilters = (getters && getters.companyConfig && getters.companyConfig.defaultCustTypeFilters) || [];

        if (defaultCustTypeFilters.length) {
          const containsBotOnlyContained = defaultCustTypeFilters.includes('BotOnlyContained');
          const containsBotOnlyEscalated = defaultCustTypeFilters.includes('BotOnlyEscalated');

          if (containsBotOnlyContained || containsBotOnlyEscalated) {
            // if either or are selected, we include 'BOT' to select the parent "VA only" option
            if (!defaultCustTypeFilters.includes('BOT')) {
              defaultCustTypeFilters.push('BOT');
            }
          } else {
            // if only one is selected, we remove 'BOT' from the array
            defaultCustTypeFilters = defaultCustTypeFilters.filter((filter) => filter != 'BOT');
          }
        }

        let companyFilters = (getters && getters.companyConfig && getters.companyConfig.filters) || null;
        if (companyFilters && Object.keys(companyFilters).length > 0 && companyFilters.businessHours) {
          dataModel.hasBusinessHours = true;
        } else {
          dataModel.hasBusinessHours = false;
        }
        dataModel.filters = {
          sources: { label: 'Conversation handled by', name: 'sources', value: defaultCustTypeFilters, type: 'checkboxSource', category: 'moreFilters' },
          languages: { label: 'Language', name: 'languages', value: [], type: 'checkbox', isRequire: true, category: 'moreFilters' },
          agentQueues: { label: 'Agent queue', name: 'agentQueues', value: null, type: 'multiSelect', placeholder: 'Search agent queue', category: 'moreFilters' },
          agentIds: { label: 'Agent ID', name: 'agentIds', value: null, type: 'multiSelect', placeholder: 'Search agent ID', category: 'moreFilters' },
          agentGroups: { label: 'Agent group', name: 'agentGroups', value: null, type: 'multiSelect', placeholder: 'Search agent group', category: 'moreFilters' },
          devices: { label: 'Device', name: 'devices', value: null, type: 'multiSelect', placeholder: 'Search devices', category: 'moreFilters' },
          custTypes: { label: 'Channel', name: 'custTypes', value: [], type: 'checkbox', isRequire: true },
          avgHandlingTime: { label: 'Average handle time between', name: 'avgHandlingTime', value: null, valueTo: null, type: 'timeRange', minValue: 0, maxValue: 10000, category: 'moreFilters' },
          maxTopics: { label: '', name: 'maxTopics', value: 100, type: 'integer', minValue: 10, maxValue: 5000, step: 10, category: 'moreFilters' },
          topics: { label: 'Topics', name: 'topics', value: null, type: 'hidden' },
          businessHoursType: { label: 'Business hours', name: 'businessHoursType', value: 0, type: 'checkbox', category: 'moreFilters' },
        };

        let res = await AnalyticsService.fetchChatFilters(getters.authUser);
        if (res && res.status == 200 && res.data) {
          await dispatch('updateLocalStorage', { key: 'agentData', data: res.data.agentData });

          // date limits
          if (res.data.dateLimits && res.data.dateLimits.length > 0) {
            dataModel.dateMin = res.data.dateLimits[0].firstDate;
            dataModel.dateMax = res.data.dateLimits[0].lastDate;
          }
          // language list
          if (dataModel.filters.languages) {
            let languageMap = WyUtils.languageMap;
            dataModel.filters.languages.dataSource = (res.data.languages || []).map((item) => {
              return { key: item.language, label: languageMap[item.language.toLowerCase()] };
            });
            dataModel.filters.languages.value = dataModel.filters.languages.dataSource.map((item) => item.key);
          }

          // device list
          if (dataModel.filters.devices) {
            dataModel.filters.devices.dataSource = (res.data.devices || []).map((item) => {
              return { key: item.device, label: item.device == '--' ? WyConstants.BLANK_LABEL : WyUtils.toCamelCase(item.device) };
            });
            dataModel.filters.devices.dataSource.unshift({ key: 'all', label: 'All' });
          }

          // agent queue list
          if (dataModel.filters.agentQueues && res.data.agentData) {
            let agentQueues = _.groupBy(res.data.agentData.filter((item) => item.aq != null), 'aq');
            dataModel.filters.agentQueues.dataSource = Object.keys(agentQueues).map((key) => {
              return { key: key, label: key == '--' ? WyConstants.BLANK_LABEL : key };
            });
            dataModel.filters.agentQueues.dataSource.unshift({ key: 'all', label: 'All' });
          }

          // agent group list
          if (dataModel.filters.agentGroups && res.data.agentData) {
            let agentGroups = _.groupBy(res.data.agentData.filter((item) => item.ag != null), 'ag');
            dataModel.filters.agentGroups.dataSource = Object.keys(agentGroups).map((key) => {
              return { key: key, label: key == '--' ? WyConstants.BLANK_LABEL : key };
            });
            dataModel.filters.agentGroups.dataSource.unshift({ key: 'all', label: 'All' });
          }

          // agent ID list
          if (dataModel.filters.agentIds && res.data.agentData) {
            let agentIds = _.groupBy(res.data.agentData.filter((item) => item.ai != null), 'ai');
            dataModel.filters.agentIds.dataSource = Object.keys(agentIds).map((key) => {
              return { key: key, label: key == '--' ? WyConstants.BLANK_LABEL : key };
            });
            dataModel.filters.agentIds.dataSource.unshift({ key: 'all', label: 'All' });
          }

          // custTypes list
          if (dataModel.filters.custTypes) {
            const custTypesMap = getters.authUser.config.app.configs.channelsMapping || {};

            dataModel.filters.custTypes.dataSource = (res.data.custTypes || []).map((item) => {
              return { key: item.custType, label: custTypesMap[item.custType] && custTypesMap[item.custType] != '' ? custTypesMap[item.custType] : WyUtils.toCamelCase(item.custType) };
            });
            dataModel.filters.custTypes.value = dataModel.filters.custTypes.dataSource.map((item) => item.key);
          }

          // channel list
          if (dataModel.filters.sources) {
            dataModel.filters.sources.dataSource = [
              // {key: 'BOT', label: 'Virtual agent only'},
              // {key: 'BotOnlyEscalated', label: 'Handoff requested, agent not connected'},
              // {key: 'BotOnlyContained', label: 'Virtual agent contained'},
              // {key: 'BotToLC', label: 'Both virtual and live agents'},
              // {key: 'LCOnly', label: ' Live agent only'}
              { key: 'BOT', label: 'VA only' },
              { key: 'BotOnlyEscalated', label: 'Live agent requested but not connected' },
              { key: 'BotOnlyContained', label: 'VA contained' },
              { key: 'BotToLC', label: 'VA engaged with live agent connected' },
              { key: 'LCOnly', label: ' Live agent only' },
            ];
          }

          // reset appliedFiltersCounter to 0
          dataModel.appliedFiltersCounter = 0;

          await dispatch('initFilterOriginalSource');
          resolve();
        } else {
          resolve();
        }
      } else {
        resolve();
      }
    });
  },

  fetchChatPerformance({ getters }, { dateRanges, topics }) {
    let payload = Object.assign(WyFilter.filterValues(getters.shareState, getters.dataModel), dateRanges);
    if (topics) {
      payload.topicDisplayNames = topics.map((item) => item.key);
      payload[getters.dataModel.filters.topics.name] = topics.reduce((list, item) => {
        item.mergedTopics.map((el) => list.push(el));
        return list;
      }, []);
    }
    return AnalyticsService.fetchChatPerformance(getters.authUser, payload);
  },

  //HOME SECTION- EXP AND AUTO PANELS
  async fetchBesBasPieChart({ getters }) {
    let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);
    let res = await AnalyticsService.fetchBesBasPieChart(getters.authUser, payload);
    let viewByData = null;
    if (res && res.data) {
      viewByData = res.data;
    }
    return viewByData;
  },
  async fetchBesBasLineChart({ getters }, { gaugeChart }) {
    let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);
    payload = { ...payload, gaugeChart: gaugeChart }
    let res = await AnalyticsService.fetchBesBasLineChart(getters.authUser, payload);
    let viewByData = null;
    if (res && res.data) {
      viewByData = res.data;
    }
    return viewByData;
  },
  async fetchBesBasWaterfallChart({ getters }, { gaugeChart }) {
    let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);
    payload = { ...payload, gaugeChart: gaugeChart }
    let res = await AnalyticsService.fetchBesBasWaterfallChart(getters.authUser, payload);
    let viewByData = null;
    if (res && res.data) {
      viewByData = res.data;
    }
    return viewByData;
  },
  async fetchConverVolLast30DaysChartData({ getters }) {
    let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);
    let res = await AnalyticsService.fetchConverVolLast30DaysChartData(getters.authUser, payload);
    let viewByData = null;
    if (res && res.data) {
      viewByData = res.data;
    }
    return viewByData;
  },
  async fetchConverVolLast3mosChartData({ getters }) {
    let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);
    let res = await AnalyticsService.fetchConverVolLast3mosChartData(getters.authUser, payload);
    let viewByData = null;
    if (res && res.data) {
      viewByData = res.data;
    }
    return viewByData;
  },

  async fetchContactReasons({ commit, getters, dispatch }, options) {
    if (getters.dataModel.dateStart && getters.dataModel.dateEnd) {
      let today = new Date();
      let results = [];

      let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);
      let res = await AnalyticsService.fetchContactReasons(getters.authUser, options, payload);

      // ampliture payload
      let ampPayload = JSON.parse(JSON.stringify(payload));
      ampPayload.dateStartCompare = moment(getters.dataModel.dateStartCompare).format('YYYY-MM-DD');
      ampPayload.dateEndCompare = moment(getters.dataModel.dateEndCompare).format('YYYY-MM-DD');

      // ampliture logging
      if (getters.moreFiltersChanged > 0) {
        // dispatch('amplitudeLog', { name: 'more_filter_applied', data: { filter_settings: ampPayload } });
      } else if (getters.dataModel.dateHasChanged) {
        getters.dataModel.dateHasChanged = false;
      } else if (getters.dataModel.filters.custTypes.hasChanged) {
        getters.dataModel.filters.custTypes.hasChanged = false;
      } else if (!options || !options.forceRefresh || options.logEvent) {
        dispatch('amplitudeLog', { name: 'topics_table_loaded', data: { filter_settings: ampPayload, data_load_time: WyUtils.getTimeDiff(today) } });
      }

      // if not cached, process the following
      if (!(res === true)) {
        if (res && res.status === 200 && res.data.results) {
          let resultsA = res.data.results;
          //let resultsB = res.data.resultsCompare;
          let sumTotalVolume = resultsA.reduce((total, item) => {
            return total + item.totalVolume;
          }, 0);
          let resultsAObj = {};
          //let resultsBObj = {};

          resultsA.forEach((item) => {
            resultsAObj[item.topicDisplayName] = item;
          });
          // if(resultsB && resultsB.length > 0) {
          //   resultsB.forEach(item => {
          //     resultsBObj[item.topicDisplayName] = item
          //   })
          // }
          Object.keys(resultsAObj).forEach((key) => {
            let item = resultsAObj[key];
            item.isSelected = false;
            item.isEditing = false;

            // prepare compare data
            // Object.keys(item).forEach(prop => {
            //   if(resultsBObj && resultsBObj[key]) {
            //     item[`${prop}Compare`] = resultsBObj[key][prop];
            //   }
            // })
            if (item.mergedTopics && item.mergedTopics.length > 1) {
              item.topicDisplayNameWithCount = item.topicDisplayName + ` (${item.mergedTopics.length})`; // showing number of merged topics
            } else {
              item.topicDisplayNameWithCount = item.topicDisplayName;
            }

            item.numOfDaysInRange = getters.dataModel.dateRangeInDays;
            item.volumePercent = ((item.totalVolume / sumTotalVolume) * 100).toFixed(2); // TODO: calculate the percent
            item.volumePercentNum = (item.totalVolume / sumTotalVolume) * 100;
            generateContactReasonsData(item, getters.xnumber);
            generatAlertIconData(item);

            // prepare trend chart
            // WyTrendChart.buildChart(item.trend, 'sumVolume').then(el => {
            //   // keep the first svg only
            //   let svgList = el.getElementsByTagName("svg");
            //   let svgSize = svgList.length;
            //   for(let i=1; i<svgSize; i++) {
            //     svgList[1].remove();
            //   }
            //   item.trendChart = el.innerHTML
            // });
            results.push(item);
          });
          let totalTopics = 0;
          if (res.data.totalTopics) {
            totalTopics = res.data.totalTopics;
          }
          commit('updateTableModelResult', {
            results: results,
            totalRecs: results.length,
            totalNumber: totalTopics,
            totalPages: 1,
          });

          return true;
        }

        state.contactReasonRunStats = null;
        return false;
      } else {
        return true;
      }
    }
  },

  async fetchContactReasonsForTargets({ getters }) {
    return AnalyticsService.fetchAllContactReasons(getters.authUser, { sortBy: 'topicCount', sortOrder: 'desc' });
  },

  mergeContactReasonsTopics({ getters }, payload) {
    if (payload && payload.topicDisplayName) {
      return AnalyticsService.mergeContactReasonsTopics(getters.authUser, payload);
    }
    return null;
  },

  unmergeContactReasons({ getters }, payload) {
    if (payload && payload.topicDisplayName && payload.topicsToUnmerge && payload.topicsToUnmerge.length > 0) {
      return AnalyticsService.mergeContactReasonsTopics(getters.authUser, payload);
    }
    return null;
  },

  updateContactReasonTopicBlackList({ getters }, payload) {
    return AnalyticsService.updateContactReasonTopicBlackList(getters.authUser, payload);
  },

  addContactReasonBlackListMessage({ getters }, message) {
    if (message && message != '') {
      return AnalyticsService.addContactReasonBlackListMessage(getters.authUser, message);
    }
    return null;
  },

  removeContactReasonBlackListMessage({ getters }, message) {
    if (message && message != '') {
      return AnalyticsService.removeContactReasonBlackListMessage(getters.authUser, message);
    }
    return null;
  },

  async updateTopicName({ getters }, topicNameObject) {
    if (topicNameObject && topicNameObject.newTopicDisplayName && topicNameObject.newTopicDisplayName.trim() != '') {
      return AnalyticsService.updateTopicName(getters.authUser, topicNameObject);
    }
    return null;
  },

  fetchContactReasonsTopicTrend({ getters }, data) {
    let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);
    payload.topics = data.topics;
    payload.topicDisplayName = data.topicDisplayName;

    return AnalyticsService.fetchContactReasonsTopicTrend(getters.authUser, payload);
  },

  fetchContactReasonsTopicMessages({ getters }, data) {
    let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);
    payload.topics = data.topics;
    payload.topicDisplayName = data.topicDisplayName;
    payload.pageNo = data.pageNo;
    payload.pageSize = data.pageSize;

    return AnalyticsService.fetchContactReasonsTopicMessages(getters.authUser, payload);
  },

  fetchContactReasonsTopicMessageConversations({ getters }, data) {
    let payload = {};
    if(data.applyGlobalFilter) {
      payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);
    }

    if(data.dateStart) payload.dateStart = data.dateStart;
    if(data.dateEnd) payload.dateEnd = data.dateEnd;
    
    payload.topics = data.topics;
    payload.topicDisplayName = data.topicDisplayName;
    payload.messageText = data.messageText;
    payload.pageNo = data.pageNo;
    payload.pageSize = 1;

    return AnalyticsService.fetchContactReasonsTopicMessageConversations(getters.authUser, payload);
  },

  fetchContactReasonsBlackListsTopicMessages({ getters }, data) {
    return AnalyticsService.fetchContactReasonsTopicMessages(getters.authUser, data);
  },

  fetchContactReasonsTopicIntents({ getters }, data) {
    let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);
    payload.topicDisplayName = data.topicDisplayName;

    return AnalyticsService.fetchContactReasonsTopicIntents(getters.authUser, payload);
  },

  fetchContactReasonsTopicEntities({ getters }, data) {
    let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);
    payload.topics = data.topics;
    payload.topicDisplayName = data.topicDisplayName;

    return AnalyticsService.fetchContactReasonsTopicEntities(getters.authUser, payload);
  },

  fetchContactReasonsTopicCxBreakdown({ getters }, data) {
    let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);

    payload = {
      ...payload,
      topicDisplayName: data.topicDisplayName,
      panelName: data.panelName,
      besScore: data.besScore,
      basScore: data.basScore,
      viewBy: data.viewBy,
    };

    return AnalyticsService.fetchContactReasonsTopicCxBreakdown(getters.authUser, payload);
  },

  fetchContactReasonsTopicAnomalies({ getters }, data) {
    let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);
    payload.topics = data.topics;
    payload.topicDisplayName = data.topicDisplayName;

    return AnalyticsService.fetchContactReasonsTopicAnomalies(getters.authUser, payload);
  },

  fetchContactReasonsBlackListsTopics({ getters }) {
    return AnalyticsService.fetchContactReasonsBlackListsTopics(getters.authUser);
  },

  fetchContactReasonsBlackListsMessages({ getters }) {
    return AnalyticsService.fetchContactReasonsBlackListsMessages(getters.authUser);
  },

  fetchContactReasonList({ getters }) {
    let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);
    return AnalyticsService.fetchContactReasonList(getters.authUser, payload);
  },

  fetchTranscriptSearchTimeRange({ getters }, topicDisplayName) {
    let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);
    payload.topicDisplayName = topicDisplayName;

    return AnalyticsService.fetchTranscriptSearchTimeRange(getters.authUser, payload);
  },

  fetchTranscriptSummary({ getters }, data) {
    let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);
    payload.conversationId = data.conversationId;
    return AnalyticsService.fetchTranscriptSummary(getters.authUser, payload);
  },

  async fetchMetricTranscriptSearch({ getters, rootState, dispatch }, data) {
    const searchActivatedBy = data.searchActivatedBy;
    delete data.searchActivatedBy;

    const filterProperties = {
      pageViewed: data.pageNo
    }

    const transcriptFilters = {
      searchByType: "Empty",
      searchKeyword: data.textQuery,
      BES_filter_is_on: false,
      BAS_filter_is_on: false,
      BES_signals: [],
      BAS_signals: [],
      topicDisplayNames: data.topicDisplayNames || []
    }

    if(data.textQuery && data.textQuery != "") {
      if(data.searchType == "1") {
        transcriptFilters.searchByType = "Keyword Search";
      }
      else if(data.searchType == "2") {
        transcriptFilters.searchByType = "Conversation ID Search";
      }
    }

    if(data.tabName == "besTab") {
      const besSignalsValues = Object.values(WyConstants.SIGNAL_PAYLOAD_MAPPER["experience"]);
      transcriptFilters.BES_signals = besSignalsValues.filter(val => data[val]);
      transcriptFilters.BES_filter_is_on = transcriptFilters.BES_signals.length > 0;
    }
    else if(data.tabName == "basTab") {
      const basSignalsValues = Object.values(WyConstants.SIGNAL_PAYLOAD_MAPPER["automation"]);
      transcriptFilters.BAS_signals = basSignalsValues.filter(val => data[val]);
      transcriptFilters.BAS_filter_is_on = transcriptFilters.BAS_signals.length > 0;
    }

    let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);

    payload = {
      ...payload,
      ...data,
      includeContactReasons: !data.contactReason || data.contactReason == '',
      fromContactReasonsDetail: data.contactReason ? true : false,
    };

    // if (payload.savedTranscripts) dispatch('logAmplitudeWithFilters', { eventName: 'view_saved_transcripts', eventProperties: { where: rootState.auth.trackInfo.where }, filterProperties: payload });
    // if (typeof payload.textQuery == 'string')
    //   dispatch('logAmplitudeWithFilters', {
    //     eventName: 'transcript_keyword_search',
    //     eventProperties: { where: rootState.auth.trackInfo.where },
    //     filterProperties: { ...payload, transcript_search_keyword: data.textQuery },
    //   });
    // if (typeof payload.textQuery == 'object')
    //   dispatch('logAmplitudeWithFilters', {
    //     eventName: 'conversation_id_search',
    //     eventProperties: { where: rootState.auth.trackInfo.where },
    //     filterProperties: { ...payload, conversation_search_id: payload.textQuery },
    //   });
    // dispatch('logAmplitudeWithFilters', { eventName: 'transcript_search_page', eventProperties: { where: rootState.auth.trackInfo.where }, filterProperties: payload });
    
    // ampliture logging
    let res = await AnalyticsService.fetchMetricTranscriptSearch(getters.authUser, payload);
    if (res && res.status == 200 && res.data) {
      // ampliture logging
      if(searchActivatedBy) {
        let num_results = res.data.totalMatchedConversations * (getters.xnumber ? getters.xnumber : 1);
        let showing_from = (data.pageNo - 1) * (data.pageSize - 1) + 1
        let showing_to = (num_results > data.pageNo * (data.pageSize - 1)) ? data.pageNo * (data.pageSize - 1) : num_results ;
        let page_viewed = data.pageNo;

        dispatch('logAmplitudeWithFilters', { eventName: 'transcripts_search_activated', 
          eventProperties: { 
            where: rootState.auth.trackInfo.where, 
            search_activated_by: searchActivatedBy, 
            transcript_filters: transcriptFilters,
            num_results: num_results,
            showing_from: num_results ? showing_from : 0,
            showing_to: num_results ? showing_to : 0,
            page_viewed : page_viewed
          } ,
          filterProperties: filterProperties
        });
      }
      return res
    }
    else {
      return null
    }
  },

  async fetchTranscriptSearch({ getters, rootState, dispatch }, data) {
    // console.log("fetchTranscriptSearch", "data")
    const searchActivatedBy = data.searchActivatedBy;
    delete data.searchActivatedBy;
    
    const filterProperties = {
      pageViewed: data.pageNo
    }

    const transcriptFilters = JSON.parse(JSON.stringify(data.transcriptSearchFilter));
    transcriptFilters.searchByType = "Empty";
    transcriptFilters.agentGroups = transcriptFilters.agentGroups || [];
    transcriptFilters.agentIds = transcriptFilters.agentIds || [];
    transcriptFilters.agentQueues = transcriptFilters.agentQueues || [];
    transcriptFilters.conversationTypes = transcriptFilters.conversationTypes || [];
    transcriptFilters.sentiment = transcriptFilters.sentiment || [];
    transcriptFilters.topicDisplayNames = transcriptFilters.topicDisplayNames || [];
    transcriptFilters.BES_filter_is_on = transcriptFilters.besSignals &&  transcriptFilters.besSignals.length > 0 || false,
    transcriptFilters.BAS_filter_is_on = transcriptFilters.basSignals  &&  transcriptFilters.basSignals.length > 0 || false,
    transcriptFilters.BES_signals = transcriptFilters.besSignals || [],
    transcriptFilters.BAS_signals  = transcriptFilters.basSignals || [],
    delete transcriptFilters.action;
    delete transcriptFilters.handOffsMax;
    delete transcriptFilters.handOffsMin;
    delete transcriptFilters.searchType;
    delete transcriptFilters.handleTimeMillisMax;
    delete transcriptFilters.handleTimeMillisMin;
    delete transcriptFilters.responseTimeMillisMax;
    delete transcriptFilters.responseTimeMillisMin;

    let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);
    payload.topics = data.topics;
    // payload.transcriptFilters = data.transcriptFilters
    payload.messageId = data.messageId;
    if (data.transcriptSearchFilter && data.transcriptSearchFilter.searchType == '2') {
      if (data.transcriptSearchFilter && data.transcriptSearchFilter.searchKeyword && data.transcriptSearchFilter.searchKeyword != '') {
        let searchKeyword = data.transcriptSearchFilter.searchKeyword.trim();
        if (searchKeyword && searchKeyword != '') {
          let searchKeywordList = searchKeyword.split(',');
          payload.textQuery = searchKeywordList;
          transcriptFilters.searchByType = "Conversation ID Search";
        }
      }
    } 
    else {
      if (data.transcriptSearchFilter && data.transcriptSearchFilter.searchKeyword && data.transcriptSearchFilter.searchKeyword != '') {
        payload.textQuery = data.transcriptSearchFilter.searchKeyword;

        transcriptFilters.searchByType = "Keyword Search";
      }
    }
    if (data.sortBy) payload.sortBy = data.sortBy;
    if (data.pageNo) payload.pageNo = data.pageNo;
    if (data.pageSize) payload.pageSize = data.pageSize;
    // payload.savedTranscript = data.savedTranscript;

    payload.includeContactReasons = !data.contactReason || data.contactReason == '';
    payload.fromContactReasonsDetail = data.contactReason ? true : false;

    if (data.date) {
      payload.dateStart = data.date;
      payload.dateEnd = data.date;
    }
    payload.from = data.from;
    if (data.transcriptSearchFilter && Object.keys(data.transcriptSearchFilter).length > 0) {
      Object.keys(data.transcriptSearchFilter).forEach((key) => {
        payload[key] = data.transcriptSearchFilter[key];
      });
    }
    let res = await AnalyticsService.fetchTranscriptSearch(getters.authUser, payload);

    if (res && res.status == 200 && res.data) {
      // ampliture logging
      if(searchActivatedBy) {
        let num_results = res.data.totalMatchedConversations * (getters.xnumber ? getters.xnumber : 1);
        let showing_from = (data.pageNo - 1) * (data.pageSize - 1) + 1
        let showing_to = (num_results > data.pageNo * (data.pageSize - 1)) ? data.pageNo * (data.pageSize - 1) : num_results ;
        let page_viewed = data.pageNo;
        dispatch('logAmplitudeWithFilters', { eventName: 'transcripts_search_activated', 
          eventProperties: { 
            where: rootState.auth.trackInfo.where, 
            search_activated_by: searchActivatedBy, 
            transcript_filters: transcriptFilters,
            num_results: num_results,
            showing_from: num_results ? showing_from : 0,
            showing_to: num_results ? showing_to : 0,
            page_viewed : page_viewed
          } ,
          filterProperties: filterProperties
        });
      }
      return res
    }
    else {
      return null
    }
  },

  async fetchTopicDisplayNameList({ getters }, data) {
    let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);
    payload.topics = data.topics;
    payload.messageId = data.messageId;
    if (data.transcriptSearchFilter && data.transcriptSearchFilter.searchType == '2') {
      if (data.transcriptSearchFilter && data.transcriptSearchFilter.searchKeyword && data.transcriptSearchFilter.searchKeyword != '') {
        let searchKeyword = data.transcriptSearchFilter.searchKeyword.trim();
        if (searchKeyword && searchKeyword != '') {
          let searchKeywordList = searchKeyword.split(',');
          payload.textQuery = searchKeywordList;
        }
      }
    } else {
      if (data.transcriptSearchFilter && data.transcriptSearchFilter.searchKeyword && data.transcriptSearchFilter.searchKeyword != '') {
        payload.textQuery = data.transcriptSearchFilter.searchKeyword;

      }
    }
    if (data.sortBy) payload.sortBy = data.sortBy;
    if (data.pageNo) payload.pageNo = data.pageNo;
    if (data.pageSize) payload.pageSize = data.pageSize;

    payload.includeContactReasons = !data.contactReason || data.contactReason == '';

    if (data.date) {
      payload.dateStart = data.date;
      payload.dateEnd = data.date;
    }
    payload.from = data.from;
    if (data.transcriptSearchFilter && Object.keys(data.transcriptSearchFilter).length > 0) {
      Object.keys(data.transcriptSearchFilter).forEach((key) => {
        payload[key] = data.transcriptSearchFilter[key];
      });
    }
    return AnalyticsService.fetchTopicDisplayNameList(getters.authUser, payload);
  },

  async fetchTopicResponseNameList({ getters }, data) {
    let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);
    payload.topics = data.topics;
    payload.messageId = data.messageId;
    if (data.transcriptSearchFilter && data.transcriptSearchFilter.searchType == '2') {
      if (data.transcriptSearchFilter && data.transcriptSearchFilter.searchKeyword && data.transcriptSearchFilter.searchKeyword != '') {
        let searchKeyword = data.transcriptSearchFilter.searchKeyword.trim();
        if (searchKeyword && searchKeyword != '') {
          let searchKeywordList = searchKeyword.split(',');
          payload.textQuery = searchKeywordList;
        }
      }
    } else {
      if (data.transcriptSearchFilter && data.transcriptSearchFilter.searchKeyword && data.transcriptSearchFilter.searchKeyword != '') {
        payload.textQuery = data.transcriptSearchFilter.searchKeyword;

      }
    }
    if (data.sortBy) payload.sortBy = data.sortBy;
    if (data.pageNo) payload.pageNo = data.pageNo;
    if (data.pageSize) payload.pageSize = data.pageSize;

    payload.includeContactReasons = !data.contactReason || data.contactReason == '';
    payload.fromContactReasonsDetail = data.contactReason ? true : false;

    if (data.date) {
      payload.dateStart = data.date;
      payload.dateEnd = data.date;
    }
    payload.from = data.from;
    if (data.transcriptSearchFilter && Object.keys(data.transcriptSearchFilter).length > 0) {
      Object.keys(data.transcriptSearchFilter).forEach((key) => {
        payload[key] = data.transcriptSearchFilter[key];
      });
    }
    return AnalyticsService.fetchTopicResponseNameList(getters.authUser, payload);
  },

  async fetchRelatedTopicList({ getters }, data) {
    let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);
    payload.topics = data.topics;
    payload.messageId = data.messageId;
    if (data.transcriptSearchFilter && data.transcriptSearchFilter.searchType == '2') {
      if (data.transcriptSearchFilter && data.transcriptSearchFilter.searchKeyword && data.transcriptSearchFilter.searchKeyword != '') {
        let searchKeyword = data.transcriptSearchFilter.searchKeyword.trim();
        if (searchKeyword && searchKeyword != '') {
          let searchKeywordList = searchKeyword.split(',');
          payload.textQuery = searchKeywordList;
        }
      }
    } else {
      if (data.transcriptSearchFilter && data.transcriptSearchFilter.searchKeyword && data.transcriptSearchFilter.searchKeyword != '') {
        payload.textQuery = data.transcriptSearchFilter.searchKeyword;

      }
    }
    if (data.sortBy) payload.sortBy = data.sortBy;
    if (data.pageNo) payload.pageNo = data.pageNo;
    if (data.pageSize) payload.pageSize = data.pageSize;

    payload.includeContactReasons = !data.contactReason || data.contactReason == '';
    payload.fromContactReasonsDetail = data.contactReason ? true : false;

    if (data.date) {
      payload.dateStart = data.date;
      payload.dateEnd = data.date;
    }
    payload.from = data.from;
    if (data.transcriptSearchFilter && Object.keys(data.transcriptSearchFilter).length > 0) {
      Object.keys(data.transcriptSearchFilter).forEach((key) => {
        payload[key] = data.transcriptSearchFilter[key];
      });
    }
    return AnalyticsService.fetchRelatedTopicList(getters.authUser, payload);
  },

  // fetchIntentNames
  async fetchIntentNames({ getters }, data) {
    let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);
    payload.topics = data.topics;
    payload.messageId = data.messageId;
    if (data.transcriptSearchFilter && data.transcriptSearchFilter.searchType == '2') {
      if (data.transcriptSearchFilter && data.transcriptSearchFilter.searchKeyword && data.transcriptSearchFilter.searchKeyword != '') {
        let searchKeyword = data.transcriptSearchFilter.searchKeyword.trim();
        if (searchKeyword && searchKeyword != '') {
          let searchKeywordList = searchKeyword.split(',');
          payload.textQuery = searchKeywordList;
        }
      }
    } else {
      if (data.transcriptSearchFilter && data.transcriptSearchFilter.searchKeyword && data.transcriptSearchFilter.searchKeyword != '') {
        payload.textQuery = data.transcriptSearchFilter.searchKeyword;

      }
    }
    if (data.sortBy) payload.sortBy = data.sortBy;
    if (data.pageNo) payload.pageNo = data.pageNo;
    if (data.pageSize) payload.pageSize = data.pageSize;

    payload.includeContactReasons = !data.contactReason || data.contactReason == '';
    payload.fromContactReasonsDetail = data.contactReason ? true : false;

    if (data.date) {
      payload.dateStart = data.date;
      payload.dateEnd = data.date;
    }
    payload.from = data.from;
    if (data.transcriptSearchFilter && Object.keys(data.transcriptSearchFilter).length > 0) {
      Object.keys(data.transcriptSearchFilter).forEach((key) => {
        payload[key] = data.transcriptSearchFilter[key];
      });
    }
    return AnalyticsService.fetchIntentNames(getters.authUser, payload);
  },

  exportBlacklistedTopicMessages({ getters }, data) {
    return AnalyticsService.exportBlacklistedTopicMessages(getters.authUser, data);
  },

  exportTranscriptSearch({ getters, rootState, dispatch }, data) {
    let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);
    payload.topics = data.topics;
    payload.messageId = data.messageId;
    if (data.transcriptSearchFilter && data.transcriptSearchFilter.searchType == '2') {
      if (data.transcriptSearchFilter && data.transcriptSearchFilter.searchKeyword && data.transcriptSearchFilter.searchKeyword != '') {
        let searchKeyword = data.transcriptSearchFilter.searchKeyword.trim();
        if (searchKeyword && searchKeyword != '') {
          let searchKeywordList = searchKeyword.split(',');
          payload.textQuery = searchKeywordList;
        }
      }
    } else {
      if (data.transcriptSearchFilter && data.transcriptSearchFilter.searchKeyword && data.transcriptSearchFilter.searchKeyword != '') {
        payload.textQuery = data.transcriptSearchFilter.searchKeyword;

      }
    }

    if (data.sortBy) payload.sortBy = data.sortBy;
    if (data.pageNo) payload.pageNo = data.pageNo;
    if (data.pageSize) payload.pageSize = data.pageSize;

    if (data.date) {
      payload.dateStart = data.date;
      payload.dateEnd = data.date;
    }
    payload.from = data.from;
    if (data.transcriptSearchFilter && Object.keys(data.transcriptSearchFilter).length > 0) {
      Object.keys(data.transcriptSearchFilter).forEach((key) => {
        payload[key] = data.transcriptSearchFilter[key];
      });
    }

    //export transcript log
    dispatch('logAmplitudeWithFilters', { eventName: 'export_transcript_search', eventProperties: { where: rootState.auth.trackInfo.where }, filterProperties: payload });
    return AnalyticsService.exportTranscriptSearch(getters.authUser, payload);
  },

  addTranscript({ getters, rootState, dispatch }, data) {
    let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);
    if (data.conversationId) payload.conversationId = data.conversationId;

    dispatch('logAmplitudeWithFilters', { eventName: 'add_transcript', eventProperties: { where: data.where || rootState.auth.trackInfo.where }, filterProperties: payload });
    return AnalyticsService.addTranscript(getters.authUser, payload);
  },

  removeTranscript({ getters, rootState, dispatch }, data) {
    let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);

    if (data.conversationId) payload.conversationId = data.conversationId;
    dispatch('logAmplitudeWithFilters', { eventName: 'remove_transcript', eventProperties: { where: data.where || rootState.auth.trackInfo.where }, filterProperties: payload });
    return AnalyticsService.removeTranscript(getters.authUser, payload);
  },

  fetchContactReasonsTopicConversations({ getters }, data) {
    let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);
    payload.topicDisplayName = data.topicDisplayName;
    payload.topics = data.topics;
    payload.messageId = data.messageId;
    if (data.from == 'relatedTopics') payload.topicDisplayName = data.topicDisplayName;
    if (data.textQuery && data.textQuery != '') payload.textQuery = data.textQuery;
    if (data.sortBy) payload.sortBy = data.sortBy;
    if (data.pageNo) payload.pageNo = data.pageNo;
    if (data.pageSize) payload.pageSize = data.pageSize;

    if (data.date) {
      payload.dateStart = data.date;
      payload.dateEnd = data.date;
    }
    payload.from = data.from;

    return AnalyticsService.fetchContactReasonsTopicConversations(getters.authUser, payload);
  },

  fetchContactReasonsTopicConversationDetail({ getters }, payload) {
    return AnalyticsService.fetchContactReasonsTopicConversationDetail(getters.authUser, payload);
  },

  fetchContactReasonsTopicConversationsWithoutFilters({ getters }, data) {
    let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);
    payload.topicDisplayName = data.topicDisplayName;
    payload.topics = data.topics;
    payload.messageId = data.messageId;
    payload.from = data.from;
    delete payload.dateStart;
    delete payload.dateEnd;
    delete payload.dateRangeInDays;
    delete payload.custTypes;
    delete payload.devices;
    delete payload.languages;
    delete payload.maxTopics;
    delete payload.sources;
    delete payload.agentGroups;
    delete payload.agentIds;
    delete payload.agentQueues;

    return AnalyticsService.fetchContactReasonsTopicConversations(getters.authUser, payload);
  },

  fetchContactReasonsTopicRelations({ getters }, data) {
    let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);
    payload.topics = data.topics;
    payload.topicDisplayName = data.topicDisplayName;

    return AnalyticsService.fetchContactReasonsTopicRelations(getters.authUser, payload);
  },

  fetchRunStats({ getters }) {
    let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);

    return AnalyticsService.fetchRunStats(getters.authUser, payload);
  },

  fetchModelStats({ getters }, data) {
    let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);
    payload.selectedStat = data.selectedStat;
    return AnalyticsService.fetchModelStats(getters.authUser, payload);
  },

  trafficManager({ getters }) {
    let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);

    return AnalyticsService.trafficManager(getters.authUser, payload);
  },

  fetchMetrics({ getters }) {
    let payload = WyFilter.filterValues(getters.shareState, getters.dataModel, WyFilter.MODE_DATE_A);

    return AnalyticsService.fetchMetrics(getters.authUser, payload);
  },

  fetchConfigIntentToHandoff({ getters }) {
    return AnalyticsService.fetchConfigIntentToHandoff(getters.authUser);
  },

  updateConfigIntentToHandoff({ getters }, payload) {
    return AnalyticsService.updateConfigIntentToHandoff(getters.authUser, payload);
  },
};

function convertToChangeValue(changeValue) {
  if (changeValue > 0) return '+';
  else if (changeValue < 0) return '-';
  else return '';
}

function generateContactReasonsData(item, xnumber) {
  let noneDataSorting = -1000000;

  // volume
  item.totalVolumeDMValue = item.totalVolume != null ? item.totalVolume * xnumber : item.totalVolume;
  if (item.totalVolumeCompare) {
    item.totalVolumeCompareDMValue = item.totalVolumeCompare != null ? item.totalVolumeCompare * xnumber : item.totalVolumeCompare;
    item.volumeChangeWithoutAbs = (item.totalVolume - item.totalVolumeCompare) / item.totalVolumeCompare;
    item.volumeChange = Math.abs(item.volumeChangeWithoutAbs);
    item.volumeChangeDisplay = convertToChangeValue(item.volumeChangeWithoutAbs) + (item.volumeChange * 100).toFixed(2);
    item.volumeChangePercent = (item.volumeChange * 100).toFixed(2);
    item.volumeChangeTooltip = generatTooltipsMsg(item.totalVolumeCompare != null ? item.totalVolumeCompare * xnumber : item.totalVolumeCompare, item.numOfDaysInRange, false);
  } else {
    item.volumeChange = null;
    item.volumeChangeWithoutAbs = noneDataSorting;
    item.volumeChangeDisplay = null;
  }

  // containment
  if (item.totalContainmentPercentCompare) {
    // item.totalPositivePercentCompare = 0
    if (item.totalContainmentPercent == parseInt(item.totalContainmentPercent)) {
      item.totalContainmentPercent = parseInt(item.totalContainmentPercent);
    }
    if (item.totalContainmentPercentCompare == parseInt(item.totalContainmentPercentCompare)) {
      item.totalContainmentPercentCompare = parseInt(item.totalContainmentPercentCompare);
    }
    item.containmentChangeWithoutAbs = (item.totalContainmentPercent - item.totalContainmentPercentCompare) / item.totalContainmentPercentCompare;
    item.containmentChange = Math.abs(item.containmentChangeWithoutAbs);
    if (item.containmentChangeWithoutAbs != parseInt(item.containmentChangeWithoutAbs)) {
      item.containmentChangeDisplay = convertToChangeValue(item.containmentChangeWithoutAbs) + (item.containmentChange * 100).toFixed(2);
      item.containmentChangePercent = (item.containmentChange * 100).toFixed(2);
    } else {
      item.containmentChangeDisplay = convertToChangeValue(item.containmentChangeWithoutAbs) + item.containmentChange * 100;
      item.containmentChangePercent = item.containmentChange * 100;
    }
    item.totalContainmentPercentNum = parseFloat(Math.abs(item.totalContainmentPercent));
    item.containmentChangeNum = parseFloat(item.containmentChange);
    item.containmentChangeTooltip = generatTooltipsMsg(item.totalContainmentPercentCompare, item.numOfDaysInRange, true);
  } else {
    if (item.totalContainmentPercent == parseInt(item.totalContainmentPercent)) {
      item.totalContainmentPercent = parseInt(item.totalContainmentPercent);
    }
    item.totalContainmentPercentNum = parseFloat(Math.abs(item.totalContainmentPercent));
    item.containmentChange = null;
    item.containmentChangeNum = null;
    item.containmentChangeWithoutAbs = noneDataSorting;
    item.containmentChangeDisplay = null;
    item.containmentChangePercent = null;
  }

  // sentiment
  if (item.totalPositivePercentCompare) {
    // item.totalPositivePercentCompare = 0
    if (item.totalPositivePercent == parseInt(item.totalPositivePercent)) {
      item.totalPositivePercent = parseInt(item.totalPositivePercent);
    }
    if (item.totalPositivePercentCompare == parseInt(item.totalPositivePercentCompare)) {
      item.totalPositivePercentCompare = parseInt(item.totalPositivePercentCompare);
    }
    item.sentimentPositiveChangeWithoutAbs = (item.totalPositivePercent - item.totalPositivePercentCompare) / item.totalPositivePercentCompare;
    item.sentimentPositiveChange = Math.abs(item.sentimentPositiveChangeWithoutAbs);
    if (item.sentimentPositiveChangeWithoutAbs != parseInt(item.sentimentPositiveChangeWithoutAbs)) {
      item.sentimentPositiveChangeDisplay = convertToChangeValue(item.sentimentPositiveChangeWithoutAbs) + (item.sentimentPositiveChange * 100).toFixed(2);
      item.sentimentPositiveChangePercent = (item.sentimentPositiveChange * 100).toFixed(2);
    } else {
      item.sentimentPositiveChangeDisplay = convertToChangeValue(item.sentimentPositiveChangeWithoutAbs) + item.sentimentPositiveChange * 100;
      item.sentimentPositiveChangePercent = item.sentimentPositiveChange * 100;
    }
    item.sentimentPositiveChangeTooltip = generatTooltipsMsg(item.totalPositivePercentCompare, item.numOfDaysInRange, true);
  } else {
    if (item.totalPositivePercent == parseInt(item.totalPositivePercent)) {
      item.totalPositivePercent = parseInt(item.totalPositivePercent);
    }
    item.sentimentPositiveChange = null;
    item.sentimentPositiveChangeWithoutAbs = noneDataSorting;
    item.sentimentPositiveChangeDisplay = null;
    item.sentimentPositiveChangePercent = null;
  }

  if (item.totalNegativePercentCompare) {
    if (item.totalNegativePercent == parseInt(item.totalNegativePercent)) {
      item.totalNegativePercent = parseInt(item.totalNegativePercent);
    }
    if (item.totalNegativePercentCompare == parseInt(item.totalNegativePercentCompare)) {
      item.totalNegativePercentCompare = parseInt(item.totalNegativePercentCompare);
    }
    item.sentimentNegativeChangeWithoutAbs = (item.totalNegativePercent - item.totalNegativePercentCompare) / item.totalNegativePercentCompare;
    item.sentimentNegativeChange = Math.abs(item.sentimentNegativeChangeWithoutAbs);
    if (item.sentimentNegativeChangeWithoutAbs != parseInt(item.sentimentNegativeChangeWithoutAbs)) {
      item.sentimentNegativeChangeDisplay = convertToChangeValue(item.sentimentNegativeChangeWithoutAbs) + (item.sentimentNegativeChange * 100).toFixed(2);
      item.sentimentNegativeChangePercent = (item.sentimentNegativeChange * 100).toFixed(2);
    } else {
      item.sentimentNegativeChangeDisplay = convertToChangeValue(item.sentimentNegativeChangeWithoutAbs) + item.sentimentNegativeChange * 100;
      item.sentimentNegativeChangePercent = item.sentimentNegativeChange * 100;
    }
    item.sentimentNegativeChangeTooltip = generatTooltipsMsg(item.totalNegativePercentCompare, item.numOfDaysInRange, true);
  } else {
    if (item.totalNegativePercent == parseInt(item.totalNegativePercent)) {
      item.totalNegativePercent = parseInt(item.totalNegativePercent);
    }
    item.sentimentNegativeChange = null;
    item.sentimentNegativeChangeWithoutAbs = noneDataSorting;
    item.sentimentNegativeChangeDisplay = null;
    item.sentimentNegativeChangePercent = null;
  }

  if (item.totalNeutralPercentCompare) {
    if (item.totalNeutralPercent == parseInt(item.totalNeutralPercent)) {
      item.totalNeutralPercent = parseInt(item.totalNeutralPercent);
    }
    if (item.totalNeutralPercentCompare == parseInt(item.totalNeutralPercentCompare)) {
      item.totalNeutralPercentCompare = parseInt(item.totalNeutralPercentCompare);
    }
    item.sentimentNeutralChangeWithoutAbs = (item.totalNeutralPercent - item.totalNeutralPercentCompare) / item.totalNeutralPercentCompare;
    item.sentimentNeutralChange = Math.abs(item.sentimentNeutralChangeWithoutAbs);
    if (item.sentimentNeutralChangeWithoutAbs != parseInt(item.sentimentNeutralChangeWithoutAbs)) {
      item.sentimentNeutralChangeDisplay = convertToChangeValue(item.sentimentNeutralChangeWithoutAbs) + (item.sentimentNeutralChange * 100).toFixed(2);
      item.sentimentNeutralChangePercent = (item.sentimentNeutralChange * 100).toFixed(2);
    } else {
      item.sentimentNeutralChangeDisplay = convertToChangeValue(item.sentimentNeutralChangeWithoutAbs) + item.sentimentNeutralChange * 100;
      item.sentimentNeutralChangePercent = item.sentimentNeutralChange * 100;
    }
    item.sentimentNeutralChangeTooltip = generatTooltipsMsg(item.totalNeutralPercentCompare, item.numOfDaysInRange, true);
  } else {
    if (item.totalNeutralPercent == parseInt(item.totalNeutralPercent)) {
      item.totalNeutralPercent = parseInt(item.totalNeutralPercent);
    }
    item.sentimentNeutralChange = null;
    item.sentimentNeutralChangeWithoutAbs = noneDataSorting;
    item.sentimentNeutralChangeDisplay = null;
  }

  // resolved
  if (item.totalResolvedCompare) {
    item.totalResolvedChangeWithoutAbs = (item.totalResolved - item.totalResolvedCompare) / item.totalResolvedCompare;
    item.totalResolvedChange = Math.abs(item.totalResolvedChangeWithoutAbs);
    item.totalResolvedChangeDisplay = convertToChangeValue(item.totalResolvedChangeWithoutAbs) + (item.totalResolvedChange * 100).toFixed(2);
    item.totalResolvedChangeTooltip = generatTooltipsMsg(item.totalResolvedCompare, item.numOfDaysInRange, false);
  } else {
    item.totalResolvedChange = null;
    item.totalResolvedChangeWithoutAbs = noneDataSorting;
    item.totalResolvedChangeDisplay = null;
  }

  // avg transfers
  if (item.avgTransfersCompare) {
    item.avgTransfersChangeWithoutAbs = (item.avgTransfers - item.avgTransfersCompare) / item.avgTransfersCompare;
    item.avgTransfersChange = Math.abs(item.avgTransfersChangeWithoutAbs);
    item.avgTransfersChangeDisplay = convertToChangeValue(item.avgTransfersChangeWithoutAbs) + (item.avgTransfersChange * 100).toFixed(2);
    item.avgTransfersChangePercent = (item.avgTransfersChange * 100).toFixed(2);
    item.avgTransfersChangeTooltip = generatTooltipsMsg(item.avgTransfersCompare, item.numOfDaysInRange, false);
    item.avgTransfersNum = Math.abs(item.avgTransfers);
    item.avgTransfersChangePercentNum = item.avgTransfersChangeWithoutAbs * 100;
  } else {
    item.avgTransfersCompare = null;
    item.avgTransfersChange = null;
    item.avgTransfersNum = Math.abs(item.avgTransfers);
    item.avgTransfersChangeWithoutAbs = noneDataSorting;
    item.avgTransfersChangeDisplay = null;
    item.avgTransfersChangePercentNum = noneDataSorting;
  }

  // avb handle time
  item.avgHandlingTimeDisplay = WyUtils.toNiceTimeFormat(item.avgHandlingTime * 60);
  if (item.avgHandlingTimeCompare) {
    item.avgHandlingTimeCompareDisplay = WyUtils.toNiceTimeFormat(item.avgHandlingTimeCompare * 60);
    item.avgHandlingTimeChangeWithoutAbs = (item.avgHandlingTime - item.avgHandlingTimeCompare) / item.avgHandlingTimeCompare;
    item.avgHandlingTimeChange = Math.abs(item.avgHandlingTimeChangeWithoutAbs);
    item.avgHandlingTimeChangeDisplay = convertToChangeValue(item.avgHandlingTimeChangeWithoutAbs) + (item.avgHandlingTimeChange * 100).toFixed(2);
    item.avgHandlingTimeChangePercent = (item.avgHandlingTimeChange * 100).toFixed(2);
    item.avgHandlingTimeChangeTooltip = generatTooltipsMsg(item.avgHandlingTimeCompareDisplay, item.numOfDaysInRange, false);
  } else {
    item.avgHandlingTimeCompareDisplay = null;
    item.avgHandlingTimeChange = null;
    item.avgHandlingTimeChangeWithoutAbs = noneDataSorting;
    item.avgHandlingTimeChangeDisplay = null;
  }

  // median handle time
  item.medianHandlingTimeDisplay = WyUtils.toNiceTimeFormat(item.medianHandlingTime * 60);
  if (item.medianHandlingTimeCompare) {
    item.medianHandlingTimeCompareDisplay = WyUtils.toNiceTimeFormat(item.medianHandlingTimeCompare * 60);
    item.medianHandlingTimeChangeWithoutAbs = (item.medianHandlingTime - item.medianHandlingTimeCompare) / item.medianHandlingTimeCompare;
    item.medianHandlingTimeChange = Math.abs(item.medianHandlingTimeChangeWithoutAbs);
    item.medianHandlingTimeChangeDisplay = convertToChangeValue(item.medianHandlingTimeChangeWithoutAbs) + (item.medianHandlingTimeChange * 100).toFixed(2);
    item.medianHandlingTimeChangeTooltip = generatTooltipsMsg(item.medianHandlingTimeCompareDisplay, item.numOfDaysInRange, false);
  } else {
    item.medianHandlingTimeCompareDisplay = null;
    item.medianHandlingTimeChange = null;
    item.medianHandlingTimeChangeWithoutAbs = noneDataSorting;
    item.medianHandlingTimeChangeDisplay = null;
  }

  // Bot Hallucination Score
  if (item.avgBhsCompare && item.avgBhsCompare > -1) {
    if (item.avgBhs == parseInt(item.avgBhs)) {
      item.avgBhs = parseInt(item.avgBhs);
    }
    if (item.avgBhsCompare == parseInt(item.avgBhsCompare)) {
      item.avgBhsCompare = parseInt(item.avgBhsCompare);
    }
    item.bhsChangeWithoutAbs = (item.avgBhs - item.avgBhsCompare) / item.avgBhsCompare;
    item.bhsChange = Math.abs(item.bhsChangeWithoutAbs);
    if (item.bhsChangeWithoutAbs != parseInt(item.bhsChangeWithoutAbs)) {
      item.bhsChangeDisplay = convertToChangeValue(item.bhsChangeWithoutAbs) + (item.bhsChange * 100).toFixed(2);
      item.bhsChangePercent = (item.bhsChange * 100).toFixed(2);
    } else {
      item.bhsChangeDisplay = convertToChangeValue(item.bhsChangeWithoutAbs) + item.bhsChange * 100;
      item.bhsChangePercent = item.bhsChange * 100;
    }
    item.bhsChangeTooltip = generatTooltipsMsg(item.avgBhsCompare, item.numOfDaysInRange, false);
  } else {
    item.avgBhsCompare = null;
    item.bhsChange = null;
    item.bhsChangeWithoutAbs = noneDataSorting;
    item.bhsChangeDisplay = null;
  }

  // agent experience
  if (item.avgAesCompare && item.avgAesCompare > -1) {
    if (item.avgAes == parseInt(item.avgAes)) {
      item.avgAes = parseInt(item.avgAes);
    }
    if (item.avgAesCompare == parseInt(item.avgAesCompare)) {
      item.avgAesCompare = parseInt(item.avgAesCompare);
    }
    item.aesChangeWithoutAbs = (item.avgAes - item.avgAesCompare) / item.avgAesCompare;
    item.aesChange = Math.abs(item.aesChangeWithoutAbs);
    if (item.aesChangeWithoutAbs != parseInt(item.aesChangeWithoutAbs)) {
      item.aesChangeDisplay = convertToChangeValue(item.aesChangeWithoutAbs) + (item.aesChange * 100).toFixed(2);
      item.aesChangePercent = (item.aesChange * 100).toFixed(2);
    } else {
      item.aesChangeDisplay = convertToChangeValue(item.aesChangeWithoutAbs) + item.aesChange * 100;
      item.aesChangePercent = item.aesChange * 100;
    }
    item.aesChangeTooltip = generatTooltipsMsg(item.avgAesCompare, item.numOfDaysInRange, false);
  } else {
    item.avgAesCompare = null;
    item.aesChange = null;
    item.aesChangeWithoutAbs = noneDataSorting;
    item.aesChangeDisplay = null;
  }

  // avg bes
  if (item.avgBesCompare && item.avgBesCompare > -1) {
    // item.totalPositivePercentCompare = 0
    if (item.avgBes == parseInt(item.avgBes)) {
      item.avgBes = parseInt(item.avgBes);
    }
    if (item.avgBesCompare == parseInt(item.avgBesCompare)) {
      item.avgBesCompare = parseInt(item.avgBesCompare);
    }
    item.besChangeWithoutAbs = (item.avgBes - item.avgBesCompare) / item.avgBesCompare;
    item.besChange = Math.abs(item.besChangeWithoutAbs);
    if (item.besChangeWithoutAbs != parseInt(item.besChangeWithoutAbs)) {
      item.besChangeDisplay = convertToChangeValue(item.besChangeWithoutAbs) + (item.besChange * 100).toFixed(2);
      item.besChangePercent = (item.besChange * 100).toFixed(2);
    } else {
      item.besChangeDisplay = convertToChangeValue(item.besChangeWithoutAbs) + item.besChange * 100;
      item.besChangePercent = item.besChange * 100;
    }
    item.avgBesNum = parseFloat(Math.abs(item.avgBes));
    item.besChangeNum = parseFloat(item.besChange);
    item.besChangeTooltip = generatTooltipsMsg(item.avgBesCompare, item.numOfDaysInRange, false);
  } else {
    if (item.avgBes == parseInt(item.avgBes)) {
      item.avgBes = parseInt(item.avgBes);
    }
    item.avgBesNum = parseFloat(Math.abs(item.avgBes));
    item.besChange = null;
    item.besChangeNum = null;
    item.besChangeWithoutAbs = noneDataSorting;
    item.besChangeDisplay = null;
    item.besChangePercent = null;
  }

  // avg bas
  if (item.avgBasCompare && item.avgBasCompare > -1) {
    // item.totalPositivePercentCompare = 0
    if (item.avgBas == parseInt(item.avgBas)) {
      item.avgBas = parseInt(item.avgBas);
    }
    if (item.avgBasCompare == parseInt(item.avgBasCompare)) {
      item.avgBasCompare = parseInt(item.avgBasCompare);
    }
    item.basChangeWithoutAbs = (item.avgBas - item.avgBasCompare) / item.avgBasCompare;
    item.basChange = Math.abs(item.basChangeWithoutAbs);
    if (item.basChangeWithoutAbs != parseInt(item.basChangeWithoutAbs)) {
      item.basChangeDisplay = convertToChangeValue(item.basChangeWithoutAbs) + (item.basChange * 100).toFixed(2);
      item.basChangePercent = (item.basChange * 100).toFixed(2);
    } else {
      item.basChangeDisplay = convertToChangeValue(item.basChangeWithoutAbs) + item.basChange * 100;
      item.basChangePercent = item.basChange * 100;
    }
    item.avgBasNum = parseFloat(Math.abs(item.avgBas));
    item.basChangeNum = parseFloat(item.basChange);
    item.basChangeTooltip = generatTooltipsMsg(item.avgBasCompare, item.numOfDaysInRange, true);
  } else {
    if (item.avgBas == parseInt(item.avgBas)) {
      item.avgBas = parseInt(item.avgBas);
    }
    item.avgBasNum = parseFloat(Math.abs(item.avgBas));
    item.basChange = null;
    item.basChangeNum = null;
    item.basChangeWithoutAbs = noneDataSorting;
    item.basChangeDisplay = null;
    item.basChangePercent = null;
  }

  // va handle time
  item.avgBotHandleTimeDisplay = item.avgBotHandleTime >= 0 ? WyUtils.toNiceTimeFormat(item.avgBotHandleTime * 60) : item.avgBotHandleTime;
  if (item.avgBotHandleTimeCompare && item.avgBotHandleTimeCompare > -1) {
    item.avgBotHandleTimeCompareDisplay = WyUtils.toNiceTimeFormat(item.avgBotHandleTimeCompare * 60);
    item.avgBotHandleTimeChangeWithoutAbs = (item.avgBotHandleTime - item.avgBotHandleTimeCompare) / item.avgBotHandleTimeCompare;
    item.avgBotHandleTimeChange = Math.abs(item.avgBotHandleTimeChangeWithoutAbs);
    item.avgBotHandleTimeChangeDisplay = convertToChangeValue(item.avgBotHandleTimeChangeWithoutAbs) + (item.avgBotHandleTimeChange * 100).toFixed(2);
    item.avgBotHandleTimeChangePercent = (item.avgBotHandleTimeChange * 100).toFixed(2);
    item.avgBotHandleTimeChangeTooltip = generatTooltipsMsg(item.avgBotHandleTimeCompareDisplay, item.numOfDaysInRange, false);
  } else {
    item.avgBotHandleTimeCompareDisplay = null;
    item.avgBotHandleTimeChange = null;
    item.avgBotHandleTimeChangeWithoutAbs = noneDataSorting;
    item.avgBotHandleTimeChangeDisplay = null;
  }

  // agent wait time
  item.avgAgentWaitTimeDisplay = item.avgAgentWaitTime >= 0 ? WyUtils.toNiceTimeFormat(item.avgAgentWaitTime * 60) : item.avgAgentWaitTime;
  if (item.avgAgentWaitTimeCompare && item.avgAgentWaitTimeCompare > -1) {
    item.avgAgentWaitTimeCompareDisplay = WyUtils.toNiceTimeFormat(item.avgAgentWaitTimeCompare * 60);
    item.avgAgentWaitTimeChangeWithoutAbs = (item.avgAgentWaitTime - item.avgAgentWaitTimeCompare) / item.avgAgentWaitTimeCompare;
    item.avgAgentWaitTimeChange = Math.abs(item.avgAgentWaitTimeChangeWithoutAbs);
    item.avgAgentWaitTimeChangeDisplay = convertToChangeValue(item.avgAgentWaitTimeChangeWithoutAbs) + (item.avgAgentWaitTimeChange * 100).toFixed(2);
    item.avgAgentWaitTimeChangePercent = (item.avgAgentWaitTimeChange * 100).toFixed(2);
    item.avgAgentWaitTimeChangeTooltip = generatTooltipsMsg(item.avgAgentWaitTimeCompareDisplay, item.numOfDaysInRange, false);
  } else {
    item.avgAgentWaitTimeCompareDisplay = null;
    item.avgAgentWaitTimeChange = null;
    item.avgAgentWaitTimeChangeWithoutAbs = noneDataSorting;
    item.avgAgentWaitTimeChangeDisplay = null;
  }

  // agent handle time
  item.avgAgentHandleTimeDisplay = item.avgAgentHandleTime >= 0 ? WyUtils.toNiceTimeFormat(item.avgAgentHandleTime * 60) : item.avgAgentHandleTime;
  if (item.avgAgentHandleTimeCompare && item.avgAgentHandleTimeCompare > -1) {
    item.avgAgentHandleTimeCompareDisplay = WyUtils.toNiceTimeFormat(item.avgAgentHandleTimeCompare * 60);
    item.avgAgentHandleTimeChangeWithoutAbs = (item.avgAgentHandleTime - item.avgAgentHandleTimeCompare) / item.avgAgentHandleTimeCompare;
    item.avgAgentHandleTimeChange = Math.abs(item.avgAgentHandleTimeChangeWithoutAbs);
    item.avgAgentHandleTimeChangeDisplay = convertToChangeValue(item.avgAgentHandleTimeChangeWithoutAbs) + (item.avgAgentHandleTimeChange * 100).toFixed(2);
    item.avgAgentHandleTimeChangePercent = (item.avgAgentHandleTimeChange * 100).toFixed(2);
    item.avgAgentHandleTimeChangeTooltip = generatTooltipsMsg(item.avgAgentHandleTimeCompareDisplay, item.numOfDaysInRange, false);
  } else {
    item.avgAgentHandleTimeCompareDisplay = null;
    item.avgAgentHandleTimeChange = null;
    item.avgAgentHandleTimeChangeWithoutAbs = noneDataSorting;
    item.avgAgentHandleTimeChangeDisplay = null;
  }

  // avg response time
  item.avgResponseTimeDisplay = WyUtils.toNiceTimeFormat(item.avgResponseTime * 60);
  if (item.avgResponseTimeCompare) {
    item.avgResponseTimeCompareDisplay = WyUtils.toNiceTimeFormat(item.avgResponseTimeCompare * 60);
    item.avgResponseTimeChangeWithoutAbs = (item.avgResponseTime - item.avgResponseTimeCompare) / item.avgResponseTimeCompare;
    item.avgResponseTimeChange = Math.abs(item.avgResponseTimeChangeWithoutAbs);
    item.avgResponseTimeChangeDisplay = convertToChangeValue(item.avgResponseTimeChangeWithoutAbs) + (item.avgResponseTimeChange * 100).toFixed(2);
    item.avgResponseTimeChangePercent = (item.avgResponseTimeChange * 100).toFixed(2);
    item.avgResponseTimeChangeTooltip = generatTooltipsMsg(item.avgResponseTimeCompareDisplay, item.numOfDaysInRange, false);
    item.avgResponseTimeChangeWithoutAbsNum = parseFloat(item.avgResponseTimeChangeWithoutAbs);
  } else {
    item.avgResponseTimeCompareDisplay = null;
    item.avgResponseTimeChange = null;
    item.avgResponseTimeChangeWithoutAbs = noneDataSorting;
    item.avgResponseTimeChangeWithoutAbsNum = noneDataSorting;
    item.avgResponseTimeChangeDisplay = null;
  }

  // median response time
  item.medianResponseTimeDisplay = WyUtils.toNiceTimeFormat(item.medianResponseTime * 60);
  if (item.medianResponseTimeCompare) {
    item.medianResponseTimeCompareDisplay = WyUtils.toNiceTimeFormat(item.medianResponseTimeCompare * 60);
    item.medianResponseTimeChangeWithoutAbs = (item.medianResponseTime - item.medianResponseTimeCompare) / item.medianResponseTimeCompare;
    item.medianResponseTimeChange = Math.abs(item.medianResponseTimeChangeWithoutAbs);
    item.medianResponseTimeChangeDisplay = convertToChangeValue(item.medianResponseTimeChangeWithoutAbs) + (item.medianResponseTimeChange * 100).toFixed(2);
    item.medianResponseTimeChangeTooltip = generatTooltipsMsg(item.medianResponseTimeCompare, item.numOfDaysInRange, false);
  } else {
    item.medianResponseTimeCompareDisplay = null;
    item.medianResponseTimeChange = null;
    item.medianResponseTimeChangeWithoutAbs = noneDataSorting;
    item.medianResponseTimeChangeDisplay = null;
  }
}

function generatAlertIconData(item) {
  item.alertsIcon = {
    icon: '',
    value: 0,
  };
  // item.dateFirstFound = '2020-11-10';
  if (item.dateFirstFound && item.dateFirstFound != '') {
    item.dateFirstFoundSorting = moment(item.dateFirstFound, 'YYYY-MM-DD').format('X');
    item.dateFirstFound = WyUtils.formatDate(item.dateFirstFound);
  }
}

function generatTooltipsMsg(compareValue, numOfDaysInRange, isPercentage) {
  let message = '';
  let percentage = '';
  if (isPercentage) {
    percentage = '%';
  }
  message = `Compared to ${compareValue + percentage} over the previous ${numOfDaysInRange} days.`;
  return message;
}

export default {
  state,
  getters,
  mutations,
  actions,
};
